import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Home, ListVideo } from 'lucide-react';
import { toast } from 'sonner';
import { useLang, tx, pickLocalized } from '../i18n/LanguageContext';
import { usePageMeta } from '../lib/seo';
import { useAuth } from '../context/AuthContext';
import { hasAccess } from '../components/LockedDialog';
import { useCourse } from '../lib/courseCache';
import { LearnSkeleton, CourseLoadState } from '../components/course/CourseLoadStates';
import { buildCurriculum } from '../lib/curriculum';
import { stripHtml } from '../lib/richText';
import { useCourseProgress } from '../lib/useCourseProgress';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';
import { LearnSidebar } from '../components/course/LearnSidebar';
import { LessonActionBar } from '../components/course/LessonSideCards';
import { LessonPlayer } from '../components/course/LessonPlayer';
import { LessonTabs } from '../components/course/LessonTabs';

export default function CourseLearn() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { lang, isRTL } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const { user } = useAuth();
  const { course, status, retry } = useCourse(id, user?.user_id);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { completed, lastId, setDone, setLast, loaded: progressLoaded } = useCourseProgress(id, user);

  const { groups, flat } = useMemo(() => buildCurriculum(course?.sections, course?.lessons, { ar: L('دروس الكورس', 'Course lessons'), en: 'Course lessons' }), [course]); // eslint-disable-line react-hooks/exhaustive-deps
  const canAccess = hasAccess(user) || !!course?.is_free;
  const isLocked = (l) => !(canAccess || l.is_free);
  const lessonId = params.get('lesson') || lastId || (flat.find(l => !isLocked(l)) || flat[0])?.id;
  const lesson = flat.find(l => l.id === lessonId) || flat[0];
  const locked = lesson ? isLocked(lesson) : false;
  usePageMeta(lesson && pickLocalized(lesson.title, lang), lesson && (pickLocalized(lesson.description, lang) || pickLocalized(course?.subtitle, lang)));

  useEffect(() => { if (progressLoaded && lesson && !locked && lesson.id !== lastId) setLast(lesson.id); }, [lesson?.id, progressLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!course) return status === 'loading' ? <LearnSkeleton /> : <CourseLoadState status={status} onRetry={retry} />;
  if (!progressLoaded) return <LearnSkeleton />;

  const go = (l) => { setParams({ lesson: l.id }); setMobileOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const prev = lesson ? flat[lesson.flatIndex - 1] : null;
  const next = lesson ? flat[lesson.flatIndex + 1] : null;
  const done = lesson && completed.has(lesson.id);
  const toggleDone = async () => {
    if (!user) { toast.info(L('سجّل الدخول لحفظ تقدمك', 'Log in to save your progress')); navigate('/login'); return; }
    await setDone(lesson.id, !done);
    if (!done && next) toast.success(L('أحسنت! انتقل للدرس التالي', 'Well done! On to the next lesson'));
  };
  const position = lesson ? L(`الدرس ${lesson.flatIndex + 1} من ${flat.length}`, `Lesson ${lesson.flatIndex + 1} of ${flat.length}`) : '';
  const Crumb = isRTL ? ChevronLeft : ChevronRight;
  const sidebarProps = { course, groups, flat, activeId: lesson?.id, completed, isLocked, onSelect: go };
  const shortDesc = lesson ? stripHtml(pickLocalized(lesson.description, lang)) : '';

  return (
    <main data-testid="course-learn-page" className="pt-[5.25rem] pb-10 min-h-screen bg-slate-50/70 overflow-x-clip">
      <div className="mx-auto w-full max-w-[1440px] px-3 sm:px-5 lg:px-6">
        <div className="flex items-center gap-2 h-10 mb-2">
          <button type="button" data-testid="learn-mobile-menu-btn" onClick={() => setMobileOpen(true)} className="lg:hidden shrink-0 h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-semibold inline-flex items-center gap-1.5">
            <ListVideo className="w-4 h-4" />{L('الدروس', 'Lessons')}
          </button>
          <nav data-testid="learn-breadcrumb" className="flex-1 min-w-0 flex items-center gap-1.5 text-[13px] text-slate-500 overflow-hidden whitespace-nowrap">
            <Link to="/" className="shrink-0 hover:text-blue-600"><Home className="w-4 h-4" /></Link><Crumb className="w-3.5 h-3.5 shrink-0" />
            <Link to="/courses" className="shrink-0 hover:text-blue-600 hidden sm:inline">{L('الكورسات', 'Courses')}</Link><Crumb className="w-3.5 h-3.5 shrink-0 hidden sm:block" />
            <Link to={`/courses/${id}`} data-testid="learn-back-btn" className="shrink-0 max-w-[40%] truncate hover:text-blue-600" dir="auto">{pickLocalized(course.brand, lang) ? `${pickLocalized(course.brand, lang)} ` : ''}{pickLocalized(course.title, lang)}</Link>
            {lesson && <><Crumb className="w-3.5 h-3.5 shrink-0" /><span className="min-w-0 truncate font-bold text-slate-800" dir="auto">{pickLocalized(lesson.title, lang)}</span></>}
          </nav>
        </div>

        <div className="grid gap-5 lg:gap-6 items-start lg:grid-cols-[380px_minmax(0,1fr)] xl:grid-cols-[440px_minmax(0,1fr)]">
          <div className="hidden lg:block lg:sticky lg:top-20"><LearnSidebar {...sidebarProps} /></div>

          <div className="min-w-0 space-y-5">
            <div className="rounded-2xl overflow-hidden shadow-[0_10px_40px_-16px_rgba(15,23,42,0.35)]"><LessonPlayer lesson={lesson} locked={locked} onEnded={() => user && !done && setDone(lesson.id, true)} /></div>
            {lesson ? (
              <>
                <div>
                  <div className="flex flex-wrap lg:flex-nowrap items-start justify-between gap-3">
                    <div className="min-w-0 lg:flex-1">
                      <span data-testid="learn-lesson-num" className="text-xs font-semibold text-blue-600">{position}</span>
                      <h1 data-testid="learn-lesson-title" className="text-xl sm:text-2xl lg:text-[1.75rem] leading-snug font-extrabold text-slate-900 break-words">{pickLocalized(lesson.title, lang)}</h1>
                    </div>
                    <LessonActionBar prev={prev} next={next} onGo={go} done={done} onToggleDone={toggleDone} locked={locked} />
                  </div>
                  {shortDesc && <p data-testid="learn-lesson-summary" className="mt-3 text-sm sm:text-[15px] text-slate-600 leading-relaxed line-clamp-2">{shortDesc}</p>}
                </div>
                <LessonTabs key={lesson.id} lesson={lesson} locked={locked} courseId={id} user={user} />
              </>
            ) : <div className="text-center text-slate-500 py-10 text-sm">{L('لا توجد دروس بعد', 'No lessons yet')}</div>}
          </div>
        </div>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side={isRTL ? 'right' : 'left'} data-testid="learn-mobile-sheet" className="p-0 w-[340px] max-w-[90vw] sm:max-w-[360px] overflow-y-auto bg-slate-50" dir={isRTL ? 'rtl' : 'ltr'}>
          <SheetTitle className="sr-only">{L('قائمة الدروس', 'Lessons menu')}</SheetTitle>
          <div className="pt-10 px-2 pb-4"><LearnSidebar {...sidebarProps} /></div>
        </SheetContent>
      </Sheet>
    </main>
  );
}
